import {
  DynamicReturnPricing,
  DynamicReturnTypes,
  PricingRuleSet,
} from "@redotech/redo-model/team";

import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyCartItem } from "@redotech/shopify-client/ajax";
import { getInternationalPrice } from "./country-check";
import { ProductType } from "./product-types";

export function getCalculatedCoveragePrice({
  basePrice,
  autocheck,
  onTapcart,
  cartValueReturnableItems,
  cartValuePackageProtectionItems,
  finalSaleReturnsCartValue,
  cartItems,
  productType,
  numValidReturnProducts,
  country,
  onlyFinalSaleReturns,
}: {
  basePrice: number;
  autocheck: WidgetAutocheck;
  onTapcart: boolean;
  cartValueReturnableItems: number;
  cartValuePackageProtectionItems: number;
  finalSaleReturnsCartValue: number;
  cartItems: ShopifyCartItem[] | undefined;
  productType: string | undefined;
  numValidReturnProducts: number;
  country?: string | null;
  onlyFinalSaleReturns: boolean;
}) {
  let returnCost = 0;
  const baseReturnPrice = onTapcart ? basePrice : basePrice / 100;

  const returnCartValue = onTapcart
    ? cartValueReturnableItems
    : cartValueReturnableItems / 100;

  const fsrCartValue = onTapcart
    ? finalSaleReturnsCartValue
    : finalSaleReturnsCartValue / 100;

  if (autocheck.dynamicReturnsPricing && cartItems) {
    returnCost = calculateDynamicReturnsPrice(
      autocheck.dynamicReturnsPricing,
      baseReturnPrice,
      numValidReturnProducts,
      returnCartValue,
    );
    returnCost = onTapcart ? returnCost : Math.floor(returnCost * 100);
  } else {
    returnCost = autocheck.pricePerOrder
      ? onTapcart
        ? parseFloat(autocheck.pricePerOrder)
        : Math.floor(parseFloat(autocheck.pricePerOrder) * 100)
      : basePrice;
  }

  if (country && autocheck.internationalReturnsPricing) {
    returnCost = getInternationalPrice(
      returnCost,
      country,
      autocheck.countries,
      autocheck.internationalReturnsPricing,
    );
  }

  let calculatedCoveragePrice = 0;
  const packageProtectionCartValue = onTapcart
    ? cartValuePackageProtectionItems
    : cartValuePackageProtectionItems / 100;

  let packageProtectionCost = getPackageProtectionPrice(
    packageProtectionCartValue,
    autocheck.packageProtection.pricingRuleSets,
    country,
    autocheck.packageProtection.minPrice,
    autocheck.packageProtection.maxPrice,
    autocheck.packageProtection.percentage,
  );
  packageProtectionCost = onTapcart
    ? packageProtectionCost
    : packageProtectionCost * 100;

  let finalSaleReturnsCost = 0;
  if (autocheck.finalSaleReturns.coverageEnabled) {
    finalSaleReturnsCost = getFinalSaleReturnsPrice(
      fsrCartValue,
      autocheck.finalSaleReturns.pricingRuleSets,
      country,
      autocheck.finalSaleReturns.maxPrice,
      autocheck.finalSaleReturns.percentage,
    );
    finalSaleReturnsCost = onTapcart
      ? finalSaleReturnsCost
      : finalSaleReturnsCost * 100;
    if (onlyFinalSaleReturns) {
      // Base price for final sale is the return price. Any additional cost should be added for final sale.
      finalSaleReturnsCost -= returnCost;
      finalSaleReturnsCost = Math.max(finalSaleReturnsCost, 0);
    }
  }

  if (productType === ProductType.RETURN_TYPE) {
    calculatedCoveragePrice = returnCost + finalSaleReturnsCost;
  }
  // Return + package Protection
  else if (productType === ProductType.BOTH_TYPE) {
    calculatedCoveragePrice =
      returnCost + // This is the base price with no package protection
      packageProtectionCost +
      finalSaleReturnsCost;
  }
  // Only package protection
  else if (productType === ProductType.PACKAGE_PROTECTION_TYPE) {
    calculatedCoveragePrice = packageProtectionCost;
  }

  return calculatedCoveragePrice;
}

// TODO: Refactor and combine with getPackageProtectionPrice
export function getFinalSaleReturnsPrice(
  cartTotal: number,
  pricingRuleSets: PricingRuleSet[] | undefined,
  country: string | undefined | null,
  maxPrice: number,
  backupPercentage: number,
) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length) ||
    cartTotal === 0
  ) {
    return 0;
  }

  if (!pricingRuleSets?.length) {
    return Math.min(cartTotal * backupPercentage, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return Math.min(price, maxPrice);
  }

  return parseFloat(
    Math.min(cartTotal * backupPercentage, maxPrice).toFixed(2),
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function getPackageProtectionPrice(
  cartTotal: number,
  pricingRuleSets: PricingRuleSet[] | undefined,
  country: string | undefined | null,
  minPrice: number,
  maxPrice: number,
  backupPercentage: number,
) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  if (!pricingRuleSets?.length) {
    return clamp(cartTotal * backupPercentage, minPrice, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(price, minPrice, maxPrice);
  }

  return parseFloat(
    clamp(cartTotal * backupPercentage, minPrice, maxPrice).toFixed(2),
  );
}

function determineRuleSet(
  pricingRuleSets: PricingRuleSet[],
  country: string | null,
) {
  for (const priceSet of pricingRuleSets) {
    if (
      country === null ||
      priceSet.countries.includes(country) ||
      priceSet.countries.length == 0
    ) {
      return priceSet;
    }
  }
  return null;
}

function getApplicableRule(ruleSet: PricingRuleSet, cartTotal: number) {
  if (!cartTotal) {
    return ruleSet.priceBrackets[0];
  }
  for (let i = 0; i < ruleSet.priceBrackets.length; i++) {
    if (i === ruleSet.priceBrackets.length - 1) {
      return ruleSet.priceBrackets[i];
    }

    if (
      cartTotal > ruleSet.priceBrackets[i].pricePoint &&
      cartTotal <= ruleSet.priceBrackets[i + 1].pricePoint
    ) {
      return ruleSet.priceBrackets[i];
    }
  }
  return ruleSet.priceBrackets[ruleSet.priceBrackets.length - 1];
}

export function calculateDynamicReturnsPrice(
  dynamicReturns: DynamicReturnPricing,
  basePrice: number,
  numItems: number,
  cartTotal: number,
) {
  if (numItems === 0) {
    return 0;
  }

  switch (dynamicReturns.type) {
    case DynamicReturnTypes.ITEM_COUNT:
      return parseFloat(
        Math.min(
          basePrice + dynamicReturns.pricePerAdditonalItem * (numItems - 1),
          dynamicReturns.maxPrice,
        ).toFixed(2),
      );
    case DynamicReturnTypes.CART_VALUE:
      return parseFloat(
        getPackageProtectionPrice(
          cartTotal,
          [dynamicReturns.pricingRuleSet],
          null,
          basePrice,
          dynamicReturns.maxPrice,
          0,
        ).toFixed(2),
      );
  }
}
